import EditorSection from "../../components/EditorSection";
import EmailChips, { emailList } from "../../components/EmailChips";
import { useEffect, useRef, useState } from "react";
import { Button, Input, Switch } from "@akin2unde/flowui-react";
import type { NotificationSetting } from "../../models/NotificationSetting";
import { NotificationSettingService } from "../../services/NotificationSettingService";
import { DataState } from "../../models/DataState";
import { useAuth } from "../../auth/useAuth";
import "./NotificationSetting.css";
const defaults: NotificationSetting = {
    Code: "",
    CartExpireDays: 30,
    AdminSaleEmail: [], AdminSaleReturnEmails: [], LowStockEmail: [], PaymentNotificationAdminEmail: [],
    SendCustomerReceiptEmail: false,
    SendAdminSaleEmail: false,
    SendLowStockEmail: false,
    LowStockReminderIntervalHours: 24,
    SendPaymentReminderBeforeDue: false,
    PaymentReminderDaysBeforeDue: 3,
    SendPaymentDueNotification: true,
    NotifyCustomerForPaymentReminder: true,
    NotifyAdminForPaymentReminder: true,
};
export default function NotificationSettingHTML() {
    const [form, setForm] = useState(defaults), [loading, setLoading] = useState(true), [resolved, setResolved] = useState(false), [error, setError] = useState(""), [saving, setSaving] = useState(false), [message, setMessage] = useState("");
    const [invalidDrafts, setInvalidDrafts] = useState<Record<string, boolean>>({});
    const chipValidity = (key: string, valid: boolean) => setInvalidDrafts(values => ({ ...values, [key]: !valid }));
    const busy = useRef(false);
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("NotificationSetting.Edit") ||
        hasPermission("Notification.Manage");
    const load = async () => {
        setLoading(true);
        setResolved(false);
        setError("");
        try {
            const p = await NotificationSettingService().GetAll(0, 2);
            if (p.TotalRecords > 1)
                throw Error("Multiple notification settings exist. Consolidate them on the backend before editing.");
            const data = p.Data[0] ?? defaults;
            setForm({ ...defaults, ...data, AdminSaleEmail: emailList(data.AdminSaleEmail), AdminSaleReturnEmails: emailList(data.AdminSaleReturnEmails), LowStockEmail: emailList(data.LowStockEmail), PaymentNotificationAdminEmail: emailList(data.PaymentNotificationAdminEmail) });
            setResolved(true);
        }
        catch (e) {
            setError(e instanceof Error
                ? e.message
                : "Could not load settings. Retry before saving.");
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, []);
    const update = (key: keyof NotificationSetting, value: unknown) => {
        setForm((x) => ({ ...x, [key]: value }));
        setMessage("");
        setError("");
    };
    const toggle = (key: keyof NotificationSetting, label: string) => (<label className="notification-toggle">
      <span>{label}</span>
      <Switch checked={Boolean(form[key])} disabled={!canEdit || saving} onChange={(checked) => update(key, checked)}/>
    </label>);
    const field = (key: keyof NotificationSetting, label: string, type: "email" | "number", disabled = false) => (<label className="notification-field">
      <span>{label}</span>
      <Input type={type} value={String(form[key] ?? "")} min={key === "PaymentReminderDaysBeforeDue" ? 0 : 1} step={1} disabled={disabled || !canEdit || saving} onChange={(e) => update(key, type === "number" ? Number(e.target.value) : e.target.value)}/>
    </label>);
    const save = async () => {
        if (busy.current || !canEdit || loading || !resolved || Object.values(invalidDrafts).some(Boolean))
            return;
        const enabledReminder = form.SendPaymentReminderBeforeDue || form.SendPaymentDueNotification;
        for (const [enabled, value, label] of [
            [form.SendAdminSaleEmail, form.AdminSaleEmail, "Admin sale emails"],
            [false, form.AdminSaleReturnEmails, "Sale return emails"],
            [form.SendLowStockEmail, form.LowStockEmail, "Low stock email"],
            [
                enabledReminder && form.NotifyAdminForPaymentReminder,
                form.PaymentNotificationAdminEmail,
                "Admin reminder email",
            ],
        ] as const) {
            if ((enabled && !value?.length) || (value ?? []).some(v => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))) {
                setError(`Enter a valid ${label.toLowerCase()}.`);
                return;
            }
        }
        for (const [value, min, label] of [
            [form.CartExpireDays, 1, "Cart expiry days"],
            [form.LowStockReminderIntervalHours, 1, "Reminder interval"],
            [form.PaymentReminderDaysBeforeDue, 0, "Days before due"],
        ] as const) {
            if (!Number.isInteger(value) || value < min) {
                setError(`${label} must be a whole number of at least ${min}.`);
                return;
            }
        }
        busy.current = true;
        setSaving(true);
        setError("");
        try {
            let existing = form;
            if (!existing.Code) {
                const page = await NotificationSettingService().GetAll(0, 2);
                if (page.TotalRecords > 1)
                    throw Error("Multiple settings exist; consolidate them before saving.");
                existing = { ...form, Code: page.Data[0]?.Code ?? "" };
            }
            const saved = await NotificationSettingService().Save([
                {
                    ...existing,
                    DataState: existing.Code ? DataState.Changed : DataState.New,
                },
            ]);
            if (!saved[0]?.Code)
                throw Error("The server returned no saved settings code. Reload before retrying.");
            setForm(saved[0]);
            setMessage("Notification settings saved successfully.");
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Could not save settings.");
        }
        finally {
            busy.current = false;
            setSaving(false);
        }
    };
    return (<section className="notification-settings">
      <header>
        <h1>Notification settings</h1>
        <p>Manage email notifications and reminders for your business.</p>
      </header>
      {loading ? (<p role="status">Loading settings…</p>) : (<>
          <div className="notification-settings-grid">
            <EditorSection className="editor-section" title={<>Sales emails</>}>
              
              {toggle("SendCustomerReceiptEmail", "Email receipt to customer")}
              {toggle("SendAdminSaleEmail", "Notify admin of new sales")}
              <EmailChips label="Admin sale emails" values={form.AdminSaleEmail} onValidationChange={valid => chipValidity("AdminSaleEmail", valid)} disabled={!canEdit || saving || !form.SendAdminSaleEmail} onChange={v => update("AdminSaleEmail", v)}/>
              <EmailChips label="Sale return notification emails" values={form.AdminSaleReturnEmails} onValidationChange={valid => chipValidity("AdminSaleReturnEmails", valid)} disabled={!canEdit || saving} onChange={v => update("AdminSaleReturnEmails", v)}/>
            </EditorSection>
            <EditorSection className="editor-section" title={<>Stock alerts</>}>
              
              {toggle("SendLowStockEmail", "Send low stock emails")}
              <EmailChips label="Low stock recipient emails" values={form.LowStockEmail} onValidationChange={valid => chipValidity("LowStockEmail", valid)} disabled={!canEdit || saving || !form.SendLowStockEmail} onChange={v => update("LowStockEmail", v)}/>
              {field("LowStockReminderIntervalHours", "Reminder interval (hours)", "number", !form.SendLowStockEmail)}
            </EditorSection>
            <EditorSection className="editor-section" title={<>Payment reminders</>}>
              
              {toggle("SendPaymentReminderBeforeDue", "Remind before due date")}
              {field("PaymentReminderDaysBeforeDue", "Days before due", "number", !form.SendPaymentReminderBeforeDue)}
              {toggle("SendPaymentDueNotification", "Notify on due date")}
              {toggle("NotifyCustomerForPaymentReminder", "Notify customer")}
              {toggle("NotifyAdminForPaymentReminder", "Notify admin")}
              <EmailChips label="Admin reminder emails" values={form.PaymentNotificationAdminEmail} onValidationChange={valid => chipValidity("PaymentNotificationAdminEmail", valid)} disabled={!canEdit || saving || !form.NotifyAdminForPaymentReminder} onChange={v => update("PaymentNotificationAdminEmail", v)}/>
            </EditorSection>
            <EditorSection className="editor-section" title={<>Cart expiry</>}>
              
              {field("CartExpireDays", "Expire unpaid carts after (days)", "number")}
            </EditorSection>
          </div>
          <div className="notification-settings-footer">
            <Button onClick={() => void save()} disabled={saving || !canEdit || !resolved || Object.values(invalidDrafts).some(Boolean)}>
              {saving ? "Saving…" : "Save settings"}
            </Button>
            {message && <span role="status">{message}</span>}
          </div>
        </>)}
      {error && (<div role="alert">
          <p>{error}</p>
          <Button variant="outline" onClick={() => void load()} disabled={saving}>
            Reload settings
          </Button>
        </div>)}
    </section>);
}
